import { DocumentNode, NetworkStatus, gql, useMutation } from '@apollo/client';
import {
  Box,
  Button,
  ButtonGroup,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Stack,
  Text,
  useBoolean,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import * as React from 'react';

import { DeleteLibraryResourceModal } from '@/components/LibraryResourceModals/DeleteLibraryResourceModal';
import { Pagination } from '@/components/Pagination';
import { PreviewRendererProps } from '@/components/PreviewRenderer/PreviewRenderer';
import { PREVIEW_RENDERER_VERSION_FILE_FRAGMENT } from '@/components/PreviewRenderer/PreviewRendererFragment';
import { logError } from '@/imports/logging/ClientLogger';
import { Card } from '@/imports/ui/chakra/components/Card';
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  FolderPlusIcon,
} from '@/imports/ui/chakra/feather';
import { BrowseItemsList } from '@/imports/ui/components/resource/BrowseItemsList';
import { BulkMoveLibraryFilesModal } from '@/imports/ui/components/resource/BulkMoveLibraryFilesModal';
import { useDebouncedValue } from '@/imports/ui/hooks/useDebouncedValue';
import { useQueryWithPastDataDeprecated } from '@/imports/ui/useQueryWithPastData';
import { useSelectionState } from '@/imports/ui/useSelectionState';

import { GroupResourcesPaneContent_singleVersionFileFragment } from './graphql-types/GroupResourcesPaneContent_singleVersionFileFragment';
import {
  GroupResourcesPane_LibraryFolderContents,
  GroupResourcesPane_LibraryFolderContentsVariables,
} from './graphql-types/GroupResourcesPane_LibraryFolderContents';
import {
  GroupResourcesPane_addFilesToGroupLibrary,
  GroupResourcesPane_addFilesToGroupLibraryVariables,
} from './graphql-types/GroupResourcesPane_addFilesToGroupLibrary';

import { LibrarySearchBar } from './components/LibrarySearchBar';
import { LibraryUploadInput } from './components/LibraryUploadInput';
import { NewFolderModal } from './components/NewFolderModal';

const ITEMS_PER_PAGE = 20;

const GET_GROUP_LIBRARY_FOLDER_CONTENTS = gql`
  query GroupResourcesPane_LibraryFolderContents(
    $input: GroupInput!
    $folderInput: LibraryFolderContentsInput!
  ) {
    group(input: $input) {
      result {
        libraryFolderContents(input: $folderInput) {
          hasMore
          totalCount
          currentFolder {
            _id
            name
            parentFolder {
              _id
              name
              parentFolder {
                _id
              }
            }
          }
          results {
            ...BrowseItemsList_libraryFolderResultsFragment
          }
        }
      }
    }
  }
  ${BrowseItemsList.libraryFolderResultsFragment}
`;

const ADD_FILES_TO_GROUP_LIBRARY_AST = gql`
  mutation GroupResourcesPane_addFilesToGroupLibrary(
    $input: AddFilesToGroupLibraryInput!
  ) {
    addFilesToGroupLibrary(input: $input)
  }
`;

type IGroupResourcesPaneDropdown = {
  loading: boolean;
  numFilesSelected: number;
  onClickMove: () => void;
  onUploaded: (options: { versionFileIds: string[] }) => void;
  onClickNewFolder?: () => void;
  onClickBulkDeleteFiles: () => void;
  hasCurrentSearch: boolean;
};

const GroupResourcesPaneDropdown: React.FC<IGroupResourcesPaneDropdown> = ({
  loading,
  numFilesSelected,
  onClickMove,
  onUploaded,
  onClickNewFolder,
  onClickBulkDeleteFiles,
  hasCurrentSearch,
}) => {
  const [isLoadingAddFiles, setIsLoadingFiles] = useBoolean(false);

  const isLoading = loading || isLoadingAddFiles;

  return (
    <ButtonGroup>
      {numFilesSelected > 0 && (
        <>
          <Button onClick={onClickBulkDeleteFiles}>Delete</Button>
          <Button onClick={onClickMove}>Move</Button>
        </>
      )}
      {!hasCurrentSearch && (
        <Menu aria-label="resources actions">
          <MenuButton
            as={Button}
            variant={numFilesSelected > 0 ? 'outline' : 'solid'}
            rightIcon={<ChevronDownIcon />}
            isLoading={isLoading}
            minWidth="85px"
          >
            Add
          </MenuButton>
          <Portal>
            <MenuList>
              <LibraryUploadInput
                onStartUploads={setIsLoadingFiles.on}
                onUploaded={(options) => {
                  onUploaded({
                    versionFileIds: options.versionFileIds,
                  });
                }}
                onEndUploads={setIsLoadingFiles.off}
                content={<Text>Add Files</Text>}
              />

              <MenuItem
                icon={<FolderPlusIcon color="gray.600" />}
                onClick={onClickNewFolder}
              >
                New Folder
              </MenuItem>
            </MenuList>
          </Portal>
        </Menu>
      )}
    </ButtonGroup>
  );
};

/* we can't load parts of react-dom-preview on the server  */
const PreviewRendererModal = dynamic<PreviewRendererProps>(
  () =>
    import('@/components/PreviewRenderer/PreviewRenderer').then(
      (mod) => mod.PreviewRenderer
    ),
  {
    ssr: false,
  }
);

export const GroupResourcesPaneContent: React.FC<{
  currentUserCanEditLibrary: boolean;
  library: {
    _id: string;
    rootFolder: {
      _id: string;
    };
  };
  groupId: string;
  singleVersionFile?: GroupResourcesPaneContent_singleVersionFileFragment | null;
  initialFolderId: string;
}> & { singleVersionFileFragment: DocumentNode } = (props) => {
  const toast = useToast();
  const deleteFilesModal = useDisclosure();
  const newFolderModal = useDisclosure();
  const router = useRouter();
  const rootFolderId = props.library.rootFolder._id;
  const [currentFolderId, setCurrentFolderId] = React.useState<string>(
    props.initialFolderId
  );

  React.useEffect(() => {
    setCurrentFolderId(props.initialFolderId);
  }, [props.initialFolderId]);

  const [bulkMoveFilesModalOpen, setBulkMoveFilesModalOpen] =
    React.useState(false);
  const [addFilesFunc, { loading: addFilesLoading }] = useMutation<
    GroupResourcesPane_addFilesToGroupLibrary,
    GroupResourcesPane_addFilesToGroupLibraryVariables
  >(ADD_FILES_TO_GROUP_LIBRARY_AST, {
    awaitRefetchQueries: true,
    refetchQueries: ['GroupResourcesPane_LibraryFolderContents'],
  });

  const [searchText, setSearchText] = React.useState('');
  const debouncedSearchText = useDebouncedValue(searchText, 500);

  // TODO (Lily): Add to URL
  const [libraryPage, setLibraryPage] = React.useState(1);

  const { error, data, networkStatus } = useQueryWithPastDataDeprecated<
    GroupResourcesPane_LibraryFolderContents,
    GroupResourcesPane_LibraryFolderContentsVariables
  >(GET_GROUP_LIBRARY_FOLDER_CONTENTS, {
    variables: {
      input: {
        _id: props.groupId,
      },
      folderInput: {
        limit: ITEMS_PER_PAGE,
        offset: (libraryPage - 1) * ITEMS_PER_PAGE,
        ...(currentFolderId !== rootFolderId && {
          libraryFolderId: currentFolderId,
        }),
        ...(debouncedSearchText && { searchTerm: debouncedSearchText }),
      },
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  if (error) {
    throw error;
  }

  const addToGroupLibraryHandler = async (options: {
    versionFileIds: string[];
  }): Promise<void> => {
    try {
      if (!currentFolderId) return;

      await addFilesFunc({
        variables: {
          input: {
            versionFileIds: options.versionFileIds,
            libraryFolderId: currentFolderId || '',
            groupId: props.groupId,
          },
        },
      });
    } catch (err) {
      logError(err);
      toast({ status: 'error', description: 'Failed to add file.' });
    }
  };

  const libraryFolderContents = data?.group.result?.libraryFolderContents;

  const currentFolder = libraryFolderContents?.currentFolder;
  const totalCount = libraryFolderContents?.totalCount || 0;

  const folderResults = libraryFolderContents?.results?.folders || [];

  const finalFolderResults = folderResults
    .filter((folder) => folder._id)
    ?.sort((a, b) => {
      const extractParts = (
        str: string
      ): [number | null, string, number | null] => {
        const cleanStr = str.replace(/[^a-zA-Z0-9]/g, ''); // Ignore special characters for sorting
        const regex = /^(\d+)?([a-zA-Z\s]+)?(\d+)?$/;
        const match = cleanStr.match(regex);

        if (!match) return [null, str, null];

        const [, numStart, text, numEnd] = match;
        return [
          numStart ? parseInt(numStart, 10) : null,
          text || '',
          numEnd ? parseInt(numEnd, 10) : null,
        ];
      };

      const [startA, textA, endA] = extractParts(a.name);
      const [startB, textB, endB] = extractParts(b.name);

      // If one starts with a number and the other doesn't, prioritize the one with a number
      if (startA !== null && startB === null) return -1;
      if (startA === null && startB !== null) return 1;

      // Compare numeric prefix if both have numbers
      if (startA !== null && startB !== null) {
        if (startA !== startB) return startA - startB;
      }

      // Compare alphabetic part
      const textCompare = textA.localeCompare(textB, undefined, {
        sensitivity: 'base',
      });
      if (textCompare !== 0) return textCompare;

      // Compare numeric suffix if both have numbers
      if (endA !== null && endB !== null) {
        return endA - endB;
      }

      return 0;
    });

  const linkResults = libraryFolderContents?.results?.files || [];
  const finalLinkResults = linkResults.filter(
    (link) => link.versionFile && link.versionFile.file
  );

  const { selection, selectAll, selectNone, toggleOne } = useSelectionState<{
    _id: string;
  }>((item) => item._id);

  const allVersionFileIds = finalLinkResults.flatMap((link) =>
    !!link.versionFile ? [link.versionFile._id] : []
  );
  // TODO (Lily): Currently all select only works for current page, would need to make backend changes to support
  // true select all
  const selectedVersionFileIds = allVersionFileIds.filter((id) =>
    selection?.allSelected
      ? !selection.excludedItems.map((item) => item._id).includes(id)
      : selection.includedItems.map((item) => item._id).includes(id)
  );

  React.useEffect(() => selectNone(), [debouncedSearchText, selectNone]);

  if (!rootFolderId) {
    return null;
  }

  const navigateToFolderHandler = (folderId: string): void => {
    setCurrentFolderId(folderId);
    if (folderId === rootFolderId) {
      const { folderId, ...query } = router.query;
      router.push({ pathname: router.pathname, query: query }, undefined, {
        shallow: true,
      });
    } else {
      router.push(
        {
          pathname: router.pathname,
          query: { ...router.query, folderId: folderId },
        },
        undefined,
        { shallow: true }
      );
    }
    selectNone();
    setSearchText('');
    setLibraryPage(1);
  };

  const versionFiles = finalLinkResults.flatMap((link) =>
    !!link.versionFile ? [link.versionFile] : []
  );

  return (
    <>
      <BulkMoveLibraryFilesModal
        groupId={props.groupId}
        versionFileIds={selectedVersionFileIds}
        open={bulkMoveFilesModalOpen}
        onClose={() => setBulkMoveFilesModalOpen(false)}
        rootFolderId={rootFolderId}
        onAfterMove={selectNone}
      />

      {currentFolder?._id && (
        <NewFolderModal
          groupId={props.groupId}
          parentLibraryFolderId={currentFolder._id}
          isOpen={newFolderModal.isOpen}
          onClose={newFolderModal.onClose}
        />
      )}

      <DeleteLibraryResourceModal
        groupId={props.groupId}
        versionFileIds={selectedVersionFileIds}
        isOpen={deleteFilesModal.isOpen}
        onClose={deleteFilesModal.onClose}
        onAfterDeleteFiles={selectNone}
      />

      {props.singleVersionFile &&
        networkStatus !== NetworkStatus.loading &&
        networkStatus !== NetworkStatus.setVariables &&
        !allVersionFileIds.includes(props.singleVersionFile._id) && (
          <PreviewRendererModal
            versionFile={props.singleVersionFile}
            onClose={() => {
              const { versionFileId, ...query } = router.query;
              router.replace(
                { pathname: router.pathname, query: query },
                undefined,
                { shallow: true }
              );
            }}
            isOpen={router.query.versionFileId === props.singleVersionFile._id}
            linkOriginalPost
          />
        )}

      {currentFolder &&
        currentFolder.parentFolder &&
        networkStatus !== NetworkStatus.loading &&
        networkStatus !== NetworkStatus.setVariables && (
          <Stack>
            <Box>
              <Button
                leftIcon={<ChevronLeftIcon />}
                variant="link"
                colorScheme="black"
                onClick={() => {
                  currentFolder.parentFolder &&
                    navigateToFolderHandler(currentFolder.parentFolder._id);
                }}
              >
                Back to{' '}
                {currentFolder.parentFolder.name === '_root'
                  ? 'Resources'
                  : currentFolder.parentFolder.name}
              </Button>
            </Box>
            <Text textStyle="xlBodyStrong">{currentFolder.name}</Text>
          </Stack>
        )}

      <Card px="6" py="4">
        <HStack spacing="4">
          <LibrarySearchBar
            value={searchText}
            folderName={
              currentFolder && currentFolder._id !== rootFolderId
                ? currentFolder.name
                : ''
            }
            onTextChange={(value) => {
              setSearchText(value);
              setLibraryPage(1);
            }}
            loading={
              !!searchText && networkStatus === NetworkStatus.setVariables
            }
          />
          {props.currentUserCanEditLibrary && (
            <GroupResourcesPaneDropdown
              onUploaded={addToGroupLibraryHandler}
              onClickMove={() => setBulkMoveFilesModalOpen(true)}
              onClickNewFolder={newFolderModal.onOpen}
              numFilesSelected={selectedVersionFileIds.length}
              loading={addFilesLoading}
              onClickBulkDeleteFiles={deleteFilesModal.onOpen}
              hasCurrentSearch={!!searchText}
            />
          )}
        </HStack>
      </Card>

      <BrowseItemsList
        groupId={props.groupId}
        folders={finalFolderResults}
        rootFolderId={rootFolderId}
        versionFiles={versionFiles}
        selection={selection}
        selectAll={selectAll}
        selectNone={selectNone}
        toggleOne={toggleOne}
        navigateToFolder={navigateToFolderHandler}
        currentUserCanEditGroupLibrary={props.currentUserCanEditLibrary}
        loading={
          networkStatus === NetworkStatus.loading ||
          networkStatus === NetworkStatus.setVariables
        }
      />

      <Pagination
        currentPage={libraryPage}
        numberOfPages={Math.ceil(totalCount / ITEMS_PER_PAGE)}
        onChange={(page) => {
          setLibraryPage(page);
          selectNone();
        }}
      />
    </>
  );
};

GroupResourcesPaneContent.singleVersionFileFragment = gql`
  fragment GroupResourcesPaneContent_singleVersionFileFragment on VersionFile {
    _id
    ...PreviewRenderer_VersionFileFragment
  }
  ${PREVIEW_RENDERER_VERSION_FILE_FRAGMENT}
`;
